'use client';
import { useState, useEffect, useRef } from 'react';

interface Milestone {
  id: string
  year: string
  title: string
  description: string
}

export default function Timeline() {
  const [visibleMilestones, setVisibleMilestones] = useState<Set<string>>(new Set())
  const timelineRef = useRef<HTMLDivElement>(null)

  const milestones: Milestone[] = [
    {
      id: 'milestone_1',
      year: '2015',
      title: 'Company Founded',
      description: 'Classic24 Digital Solutions begins its journey with a small team and big dreams.',
    },
    {
      id: 'milestone_2',
      year: '2017',
      title: '100+ Clients',
      description: 'Reached our first major milestone, serving over 100 satisfied clients.',
    },
    {
      id: 'milestone_3',
      year: '2019',
      title: 'Service Expansion',
      description: 'Expanded services to include app development and comprehensive design solutions.',
    },
    {
      id: 'milestone_4',
      year: '2021',
      title: '500+ Projects',
      description: 'Successfully delivered 500+ projects across diverse industries.',
    },
    {
      id: 'milestone_5',
      year: '2023',
      title: 'Industry Recognition',
      description: 'Awarded "Best Digital Agency" for outstanding client results and innovation.',
    },
    {
      id: 'milestone_6',
      year: '2026',
      title: 'Future Forward',
      description: 'Continuing to push boundaries with AI-powered solutions and global expansion.',
    },
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-milestone-id')
            if (id) {
              setVisibleMilestones((prev) => new Set(prev).add(id))
            }
          }
        })
      },
      { threshold: 0.3 }
    )

    const milestoneElements = timelineRef.current?.querySelectorAll('[data-milestone-id]')
    milestoneElements?.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <section className="py-20 md:py-32 bg-zinc-950 border-y border-zinc-800">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-[2px] h-4 bg-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
              Our Journey
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            A Decade of<br />Digital Innovation
          </h2>
        </div>

        <div ref={timelineRef} className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-[2px] bg-zinc-800" />

          <div className="space-y-12">
            {milestones.map((milestone, idx) => (
              <div
                key={milestone.id}
                data-milestone-id={milestone.id}
                className={`relative flex items-center ${
                  idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Dot */}
                <div
                  className={`absolute left-8 md:left-1/2 w-4 h-4 rounded-full border-2 transition-all duration-500 ${
                    visibleMilestones.has(milestone.id)
                      ? 'bg-amber-500 border-amber-500 scale-100' :'bg-zinc-900 border-zinc-700 scale-75'
                  }`}
                  style={{ transform: 'translateX(-50%)' }}
                />

                {/* Content Card */}
                <div
                  className={`w-full md:w-[calc(50%-4rem)] ml-16 md:ml-0 ${
                    idx % 2 === 0 ? 'md:mr-auto md:pr-12' : 'md:ml-auto md:pl-12'
                  }`}
                >
                  <div
                    className={`bg-zinc-900 border border-zinc-800 rounded-2xl p-6 transition-all duration-500 ${
                      visibleMilestones.has(milestone.id)
                        ? 'opacity-100 translate-y-0 border-amber-500/30' :'opacity-0 translate-y-8'
                    }`}
                  >
                    <div className="text-3xl font-bold text-amber-500 mb-2">
                      {milestone.year}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}